/**
 * DIFF-4 seed — Agentic Shipment Status.
 *
 * 3 exception prediction scenarios (critical, warning, info) + matching
 * proactive_notifications rows so the log has realistic history on first load.
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const T = DEMO_TENANT_ID;
const iso = (days = 0, hours = 0, minutes = 0) =>
  new Date(Date.now() - (days * 86400000 + hours * 3600000 + minutes * 60000)).toISOString();
const future = (hours = 0) => new Date(Date.now() + hours * 3600000).toISOString();

export const SEED_EXCEPTION_PREDICTIONS = [
  // Critical — already acted on (shows end-to-end workflow)
  {
    id: 'pred-0001', tenantId: T,
    loadId: 'load-00080', loadNumber: 'WC-10780',
    customerId: 'cust-pacific-fresh', customerName: 'Pacific Fresh Foods',
    carrierId: 'carrier-swft', carrierName: 'Swift Transportation',
    probability: 82, horizonHours: 4, severity: 'critical', status: 'acted_on',
    predictedAt: iso(0, 2),
    predictedForRangeStart: future(4), predictedForRangeEnd: future(6),
    factors: [
      { signal: 'low_trust_carrier',        detail: 'Carrier trust 48 · restricted', weight: 30 },
      { signal: 'delivery_window_imminent', detail: '3h to SLA',                     weight: 30 },
      { signal: 'lane_history',             detail: '4 prior exceptions on IL→TX',   weight: 15 },
      { signal: 'hos_expiring',             detail: '1.5h HOS remaining',            weight: 20 },
    ],
    actedAt: iso(0, 1, 40), actedBy: 'aegis',
    actionSummary: 'voice:queued sms:queued email:queued',
    isDeleted: false, createdAt: iso(0, 2), updatedAt: iso(0, 1, 40), version: 2, source: 'agent_status_engine',
  },
  // Warning — open, waiting for operator
  {
    id: 'pred-0002', tenantId: T,
    loadId: 'load-00081', loadNumber: 'WC-10781',
    customerId: 'cust-heartland-grain', customerName: 'Heartland Grain Co',
    carrierId: 'carrier-usat', carrierName: 'USA Truck',
    probability: 58, horizonHours: 5, severity: 'warning', status: 'open',
    predictedAt: iso(0, 0, 35),
    predictedForRangeStart: future(5), predictedForRangeEnd: future(7),
    factors: [
      { signal: 'pickup_overdue',   detail: '4h past scheduled pickup',        weight: 24 },
      { signal: 'low_trust_carrier', detail: 'Carrier trust 58 · watchlist',   weight: 12 },
      { signal: 'lane_history',      detail: '2 prior exceptions on KS→OK',    weight: 15 },
    ],
    isDeleted: false, createdAt: iso(0, 0, 35), updatedAt: iso(0, 0, 35), version: 1, source: 'agent_status_engine',
  },
  // Info — open, lower priority
  {
    id: 'pred-0003', tenantId: T,
    loadId: 'load-00082', loadNumber: 'WC-10782',
    customerId: 'cust-sunrise-beverages', customerName: 'Sunrise Beverages',
    carrierId: 'carrier-schneider', carrierName: 'Schneider',
    probability: 42, horizonHours: 6, severity: 'info', status: 'open',
    predictedAt: iso(0, 0, 18),
    predictedForRangeStart: future(6), predictedForRangeEnd: future(8),
    factors: [
      { signal: 'delivery_window_imminent', detail: '5h to SLA',                weight: 30 },
      { signal: 'lane_history',             detail: '2 prior exceptions',       weight: 12 },
    ],
    isDeleted: false, createdAt: iso(0, 0, 18), updatedAt: iso(0, 0, 18), version: 1, source: 'agent_status_engine',
  },
];

export const SEED_PROACTIVE_NOTIFICATIONS = [
  // pred-0001 → voice check call
  {
    id: 'pn-0001', tenantId: T,
    loadId: 'load-00080', loadNumber: 'WC-10780', predictionId: 'pred-0001',
    channel: 'voice', target: 'carrier', targetName: 'Swift Transportation',
    to: '+1-520-555-0117',
    status: 'completed', mock: true, providerCallId: 'mock-CALL0001',
    message: '[MOCK] AEGIS dialed Swift Transportation — carrier confirmed 90-minute delay. New ETA 15:45 CT.',
    triggeredBy: 'aegis', triggeredAt: iso(0, 1, 40),
    isDeleted: false, createdAt: iso(0, 1, 40), updatedAt: iso(0, 1, 40), version: 1, source: 'agent_status_engine',
  },
  // pred-0001 → SMS to shipper
  {
    id: 'pn-0002', tenantId: T,
    loadId: 'load-00080', loadNumber: 'WC-10780', predictionId: 'pred-0001',
    channel: 'sms', target: 'shipper', targetName: 'Shipper',
    to: '+1-708-555-0022',
    status: 'delivered', mock: true, providerMessageId: 'mock-SM00002',
    message: 'AEGIS: load WC-10780 82% risk of delay next 4h. Calling carrier now.',
    triggeredBy: 'aegis', triggeredAt: iso(0, 1, 40),
    isDeleted: false, createdAt: iso(0, 1, 40), updatedAt: iso(0, 1, 39), version: 1, source: 'agent_status_engine',
  },
  // pred-0001 → email to customer
  {
    id: 'pn-0003', tenantId: T,
    loadId: 'load-00080', loadNumber: 'WC-10780', predictionId: 'pred-0001',
    channel: 'email', target: 'customer', targetName: 'Pacific Fresh Foods',
    to: 'ops@pacificfresh.example.com',
    status: 'delivered', mock: true, providerMessageId: 'mock-EM00003',
    message: 'Proactive notice: possible delay on WC-10780',
    triggeredBy: 'aegis', triggeredAt: iso(0, 1, 39),
    isDeleted: false, createdAt: iso(0, 1, 39), updatedAt: iso(0, 1, 38), version: 1, source: 'agent_status_engine',
  },
  // Older successful proactive push (different load)
  {
    id: 'pn-0004', tenantId: T,
    loadId: 'load-00075', loadNumber: 'WC-10775', predictionId: null,
    channel: 'voice', target: 'carrier', targetName: 'XPO Logistics',
    to: '+1-248-555-0198',
    status: 'completed', mock: true, providerCallId: 'mock-CALL0004',
    message: '[MOCK] AEGIS dialed XPO — driver on track, ETA confirmed.',
    triggeredBy: 'aegis', triggeredAt: iso(0, 6),
    isDeleted: false, createdAt: iso(0, 6), updatedAt: iso(0, 6), version: 1, source: 'agent_status_engine',
  },
  {
    id: 'pn-0005', tenantId: T,
    loadId: 'load-00075', loadNumber: 'WC-10775', predictionId: null,
    channel: 'email', target: 'customer', targetName: 'Keyston Bros',
    to: 'dispatch@keystonbros.example.com',
    status: 'delivered', mock: true, providerMessageId: 'mock-EM00005',
    message: 'ETA confirmation for WC-10775',
    triggeredBy: 'aegis', triggeredAt: iso(0, 6),
    isDeleted: false, createdAt: iso(0, 6), updatedAt: iso(0, 6), version: 1, source: 'agent_status_engine',
  },
  // SMS that actually went through live (non-mock) — for realism
  {
    id: 'pn-0006', tenantId: T,
    loadId: 'load-00072', loadNumber: 'WC-10772', predictionId: null,
    channel: 'sms', target: 'shipper', targetName: 'Shipper',
    to: '+1-312-555-0143',
    status: 'delivered', mock: false, providerMessageId: 'SM12abcd3456',
    message: 'AEGIS: load WC-10772 arrived at destination. POD attached.',
    triggeredBy: 'aegis', triggeredAt: iso(1, 2),
    isDeleted: false, createdAt: iso(1, 2), updatedAt: iso(1, 2), version: 1, source: 'agent_status_engine',
  },
];

export const AGENT_STATUS_SEED_SUMMARY = {
  exception_predictions: SEED_EXCEPTION_PREDICTIONS.length,
  proactive_notifications: SEED_PROACTIVE_NOTIFICATIONS.length,
};
